<template>
    <div class="video-page">
        <div ref="$video" class="video-page__video" :class="{ 'video-page__video--sticking': videoIsSticking }">
            <div class="video-page__video-wrapper">
                <BunnyVideo :bunny-id="video.bunny_id" />
                <BaseButton
                    v-if="videoIsSticking"
                    class="video-page__sticky-close base-button--icon-only base-button--outline"
                    icon-left="cross"
                    @click="hideStickyVideo"
                />
            </div>
        </div>

        <div class="video-page__header">
            <div class="video-page__meta">
                <VideoDetails :video="video" />
            </div>
            <VideoActions
                v-if="!inReview"
                class="video-page__actions"
                :video="video"
            />
            <VideoReview
                v-if="inReview"
                :video="video"
                :review-status="reviewStatus"
            />
        </div>

        <div class="video-page__body">
            <div
                v-if="!inReview && !authStore.isSponsor"
                class="video-page__comments"
            >
                <h2 v-if="comments?.length > 1" class="comments__title">{{ comments?.length }} comments</h2>
                <h2 v-if="comments?.length === 1" class="comments__title">{{ comments?.length }} comment</h2>
                <h2 v-if="comments?.length === 0" class="comments__title">No comments yet</h2>

                <BaseSpinner v-if="commentStore.pending" class="video-page__spinner" />
                <CommentInput
                    :video-id="video.id"
                />
                <template v-if="comments?.length">
                    <CommentThread
                        v-for="comment in comments.slice(0, visibleComments)"
                        :key="comment.id"
                        :comment="comment"
                    />
                    <BaseButton
                        v-if="comments.length > visibleComments"
                        class="base-button--outline"
                        @click="visibleComments += 5"
                    >
                        Load more comments
                    </BaseButton>
                </template>
            </div>
        </div>

        <BaseSection
            v-if="relatedVideos?.length && !inReview"
            data-span-all
            class="video-page__related"
        >
            <template #title>Related videos</template>
            <template #default>
                <VideoCardList
                    :videos="relatedVideos"
                    appearance="triple"
                />
            </template>
        </BaseSection>
        <QRModal :video="video" />
        <CiteModal :video="video" />
        <VideoReviewModal :video="video" />
    </div>
</template>

<script setup>
import { useElementBounding, useIntervalFn } from '@vueuse/core';
import VideoQuery from '~/graphql/queries/getVideoBySlug.graphql';
import CreateViewMutation from '~/graphql/mutations/createView.graphql';
import RelatedVideosQuery from '~/graphql/queries/videos.graphql';

import { useCommentStore } from '~/store/comment';
import { useAuthStore } from '~/store/auth.js';
const { push } = useTagmanager();

defineProps({
    reviewStatus: {
        type: String,
        default: null
    }
});

const $video = ref(null);
const { top, height } = useElementBounding($video);
const commentStore = useCommentStore();
const authStore = useAuthStore();
const route = useRoute();

const { data } = await useAsyncQuery({
    query: VideoQuery,
    variables: { slug: route.params.slug[0] }
});

if (!toValue(data)?.video) {
    throw createError({
        statusCode: 404
    });
}

const video = computed(() => data.value?.video);

const { data: relatedVideosResponse } = await useAsyncQuery({
    query: RelatedVideosQuery,
    variables: {
        limit: 3,
        excludeVideoIds: [video.value?.id],
        specialisation: video.value?.specialisations[0].id
    }
});

const relatedVideos = relatedVideosResponse.value?.videos?.data;

const comments = computed(() => {
    return commentStore.comments;
});

const videoIsSticking = computed(() => {
    return (top.value === 0 || top.value < height.value * -1) && videoStickyEnabled.value;
});

const inReview = computed(() => video.value?.status === 'in-review');

const visibleComments = ref(3);

const videoStickyEnabled = ref(true);

commentStore.fetchComments(video.value?.id);

useIntervalFn(() => {
    commentStore.fetchComments(video.value?.id);
}, 3000, { immediateCallback: false });

function hideStickyVideo() {
    videoStickyEnabled.value = false;
}

const { execute: createViewExecute } = useAsyncMutation('create-view');

onMounted(() => {
    if (video.value) {
        createViewExecute({
            mutation: CreateViewMutation,
            variables: {
                videoId: video.value?.id
            }
        });
    }
    push({
        event: 'video_page_view'
    });
});

useVideoSeo(video.value);
</script>

<style lang="less" src="./VideoPage.less" />

<style lang="less" scoped>
.video-page__video {
    @media @q-md-min {
        position: relative;
    }
}

.video-page__sticky-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 1;
}
</style>
